import CopyToClipboardIcon from 'components/util/CopyToClipboard';
import { getLinks } from 'pages/matters/util';
import { ReactNode } from 'react';
import { ClassifierData } from 'types/classifiers';
import { CustomField, CustomFieldType } from 'types/customFields';
import { MinDto } from 'types/other';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getClassifierText, t } from 'utils/getTranslations';

interface Props {
    field: CustomField;
    classifiers: ClassifierData[];
}

const isMinDto = (value: unknown): value is MinDto =>
    typeof value === 'object' && value !== null && 'id' in value && 'name' in value;

const toDisplayString = (value: unknown): string =>
    typeof value === 'string' || typeof value === 'number' ? String(value) : '';

const CustomFieldDisplay = ({ field, classifiers: allClassifiers }: Props) => {
    let content: string | ReactNode;

    switch (field.fieldType) {
        case CustomFieldType.CLASSIFIER: {
            const classifierValues = field.isMultivalue ? (field.values ?? []) : [field.value];
            const classifiers = allClassifiers.find((classifier) => classifier.code === field.classifierCode);

            content =
                classifierValues
                    .map((value) => getClassifierText(classifiers, typeof value === 'string' ? value : undefined))
                    .sort((a, b) => a.localeCompare(b))
                    .join(',\u00A0') || '-';

            break;
        }
        case CustomFieldType.DATE: {
            const dateValue = field.isMultivalue ? undefined : field.value;
            content =
                typeof dateValue === 'string' || typeof dateValue === 'number' || dateValue instanceof Date
                    ? formatDateStringInput(new Date(dateValue))
                    : '-';
            break;
        }
        case CustomFieldType.BOOLEAN:
            content = field.isMultivalue ? '-' : field.value ? t('yes') : t('no');
            break;
        case CustomFieldType.EMPLOYEE: {
            const candidates: unknown[] = field.isMultivalue ? (field.values ?? []) : field.value ? [field.value] : [];
            const links = getLinks('employee', candidates.filter(isMinDto));
            content = links.length > 0 ? links : '-';
            break;
        }
        case CustomFieldType.DECIMAL:
        case CustomFieldType.INTEGER:
        case CustomFieldType.TEXTAREA:
        case CustomFieldType.TEXTINPUT:
            content = field.isMultivalue ? '-' : toDisplayString(field.value) || '-';
            break;
        default:
            content = '-';
    }

    // Copy-to-clipboard is offered only for fields that opt in and resolve to a plain, non-empty
    // text value (so it never attaches to employee links or empty "-" placeholders).
    const copyText = field.showCopyIcon && typeof content === 'string' && content !== '-' ? content : '';
    if (copyText) {
        return (
            <span className="inline-flex items-center">
                {content}
                <CopyToClipboardIcon text={copyText} />
            </span>
        );
    }

    return <>{content}</>;
};

export default CustomFieldDisplay;
